// The Sharee's own cards, saved device-locally (IndexedDB). This is what makes
// Save work with no login at all.
//
// A person can hold several — a work card, a personal one, one for an event —
// each with its own public id and therefore its own NFC tag. They are keyed by
// that id, and one is "active": the one the My Card tab shows and the one a
// bare save updates.
//
// Kept in its own DB and typed as `unknown` so it does not import the Card type
// from pocketbase.ts (which imports the data layer) — no import cycle.
import { openDB, type IDBPDatabase } from 'idb';
import { newPublicId } from './id';

const DB_NAME = 'smartcard-mycard';
/** v1 store: a single card under a fixed key. Read once, to migrate. */
const LEGACY = 'mycard';
const LEGACY_KEY = 'me';
/** v2 stores: cards keyed by public id, plus which one is active. */
const CARDS = 'cards';
const META = 'meta';
const ACTIVE_KEY = 'activeId';

type Row = Record<string, unknown>;

let dbP: Promise<IDBPDatabase> | null = null;

function db(): Promise<IDBPDatabase> {
  if (!dbP) {
    dbP = openDB(DB_NAME, 2, {
      async upgrade(database, oldVersion, _newVersion, tx) {
        if (oldVersion < 1) database.createObjectStore(LEGACY);
        if (oldVersion < 2) {
          database.createObjectStore(CARDS);
          database.createObjectStore(META);

          // Carry the single v1 card into the new list. Without this, upgrading
          // would silently orphan the card someone has already printed onto a
          // tag — the record still resolves on the server, but this device
          // would forget its edit token and stop being able to change it.
          const legacy = (await tx.objectStore(LEGACY).get(LEGACY_KEY)) as Row | undefined;
          const id = legacy && typeof legacy.id === 'string' ? legacy.id : '';
          if (legacy && id) {
            await tx.objectStore(CARDS).put(legacy, id);
            await tx.objectStore(META).put(id, ACTIVE_KEY);
          }
        }
      },
    });
  }
  return dbP;
}

/**
 * Save (upsert) one of the Sharee's cards, and make it the active one.
 *
 * The card's id is its identity. A card that carries one updates that card; a
 * card with no id is a NEW card and gets a fresh id. That distinction is what
 * makes several cards possible, and it is why nothing here falls back to "the
 * last card saved" — doing so would silently overwrite one card while the
 * person was creating another.
 *
 * The edit token is carried forward for the same card, because losing it would
 * strand the card: it could no longer be edited in place, so the next save
 * would publish a new one under a new id and every programmed tag would go
 * stale.
 */
export async function saveMyCard(card: unknown): Promise<void> {
  const database = await db();
  const c = { ...(card as Row) };
  const id = (typeof c.id === 'string' && c.id) || newPublicId();
  c.id = id;
  c.publicId = id;

  const prev = (await database.get(CARDS, id)) as Row | undefined;
  if (!c.editToken && typeof prev?.editToken === 'string') {
    c.editToken = prev.editToken;
  }
  // Stamped once, then preserved: the switcher orders by it, and cards that
  // reshuffled every time one was edited would be hard to keep track of.
  c.createdAt = typeof prev?.createdAt === 'number' ? prev.createdAt : Date.now();

  const tx = database.transaction([CARDS, META], 'readwrite');
  await tx.objectStore(CARDS).put(c, id);
  await tx.objectStore(META).put(id, ACTIVE_KEY);
  await tx.done;
}

/** Every card on this device, oldest first — a stable order, so the switcher
 *  does not reshuffle when a card is edited. */
export async function listMyCards<T = unknown>(): Promise<T[]> {
  const database = await db();
  const all = (await database.getAll(CARDS)) as Row[];
  return all.sort((a, b) => Number(a.createdAt ?? 0) - Number(b.createdAt ?? 0)) as T[];
}

/** The active card, or null when there are none. */
export async function loadMyCard<T = unknown>(): Promise<T | null> {
  const database = await db();
  const id = (await database.get(META, ACTIVE_KEY)) as string | undefined;
  if (id) {
    const card = (await database.get(CARDS, id)) as T | undefined;
    if (card) return card;
  }
  // The pointer is missing or stale (the card was deleted elsewhere). Fall back
  // to the first card rather than reporting none, so a person is never told
  // they have no card while one is sitting in the list.
  const [first] = await listMyCards<T>();
  return first ?? null;
}

/** Which card is active, or '' if there are none. */
export async function activeCardId(): Promise<string> {
  const database = await db();
  return ((await database.get(META, ACTIVE_KEY)) as string | undefined) ?? '';
}

/** Switch which card the app shows. Ignores an id that is not on this device. */
export async function setActiveCard(id: string): Promise<void> {
  const database = await db();
  if (!(await database.get(CARDS, id))) return;
  await database.put(META, id, ACTIVE_KEY);
}

/** Forget a card on this device.
 *
 *  Local only: the published card keeps resolving, because someone may already
 *  have tapped its tag and collected it. Deleting it here removes the edit
 *  token, so the card becomes permanently uneditable from this device — the
 *  caller is responsible for confirming that. */
export async function deleteMyCard(id: string): Promise<void> {
  const database = await db();
  const tx = database.transaction([CARDS, META], 'readwrite');
  await tx.objectStore(CARDS).delete(id);
  if ((await tx.objectStore(META).get(ACTIVE_KEY)) === id) {
    await tx.objectStore(META).delete(ACTIVE_KEY);
  }
  await tx.done;
}

/** Test/utility: forget every card. */
export async function clearMyCard(): Promise<void> {
  const database = await db();
  const tx = database.transaction([CARDS, META], 'readwrite');
  await tx.objectStore(CARDS).clear();
  await tx.objectStore(META).clear();
  await tx.done;
}
